// src/components/fix-actions/suggested-actions-smart-inbox.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Columns2, LayoutList } from "lucide-react";
import type { FixAction } from "../../services/fix-actions/fix-actions-service";
import { fixActionsService, artifactTracker } from "../../services/fix-actions/fix-actions-service";
import { ActionItemCompactRow } from "./action-item-compact-row";
import { ActionItemDetailPanel } from "./action-item-detail-panel";
import { useSettings } from "@/app/hooks/common/useSettings";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";

type InboxLayout = "split" | "accordion";

const RAIL_MIN = 280;
const RAIL_MAX = 520;
const RAIL_DEFAULT = 360;

function actionKey(a: FixAction): string {
  const src = String(a.sourceModule || "suggested").toLowerCase();
  const title = String(a.title || "").trim().toLowerCase();
  return title ? `${src}::${title}` : `${src}::#${a.id}`;
}

function railLsKey(brandId: string): string {
  return `fix_actions_inbox_rail_${brandId || "default"}`;
}

function readRailWidth(brandId: string): number {
  try {
    const raw = Number(localStorage.getItem(railLsKey(brandId)));
    if (Number.isFinite(raw) && raw >= RAIL_MIN && raw <= RAIL_MAX) return raw;
  } catch {}
  if (typeof window !== "undefined" && window.innerWidth >= 1400) return 400;
  return RAIL_DEFAULT;
}

interface SuggestedActionsSmartInboxProps {
  actions: FixAction[];
  onExplainWithAI: (topic: string) => void;
  onGenerateAsset: (
    actionId: number,
    actionTitle: string,
    actionTheme: string,
    artifactTitle: string,
    artifactType: string,
    artifactDescription: string,
    artifactIndex: number,
    totalArtifacts?: number
  ) => void;
  onNavigateToStudio?: (artifactId: string) => void;
  isSidebarCollapsed: boolean;
  highlightActionTitle?: string | null;
  trackerVersion?: number;
  brandId: string;
}

export function SuggestedActionsSmartInbox({
  actions,
  onExplainWithAI,
  onGenerateAsset,
  onNavigateToStudio,
  isSidebarCollapsed,
  highlightActionTitle,
  trackerVersion,
  brandId,
}: SuggestedActionsSmartInboxProps) {
  // ── Layout: user setting → but FORCED to accordion on mobile ──
  // Rationale: the "Split" layout relies on a 280–520px resizable left rail
  // + detail on the right, which is physically impossible on a ~380px
  // viewport. Accordion is the only sensible mobile layout.
  const { settings, updateAppearance } = useSettings();
  const { isMobile } = useResponsive();

  const userLayout: InboxLayout =
    (settings?.appearance?.inboxLayout as InboxLayout) === "accordion"
      ? "accordion"
      : "split";

  const layout: InboxLayout = isMobile ? "accordion" : userLayout;

  const setLayout = (next: InboxLayout) => {
    // User toggling the layout is only meaningful on sm+; on mobile we
    // keep accordion regardless. Still persist the preference so when they
    // return to a wider viewport the saved choice holds.
    if (next === userLayout) return;
    updateAppearance({ inboxLayout: next }).catch(() => {});
  };

  // ── Resizable rail (split mode only, desktop only) ──
  const [railWidth, setRailWidth] = useState<number>(() => readRailWidth(brandId));
  useEffect(() => setRailWidth(readRailWidth(brandId)), [brandId]);
  useEffect(() => {
    try {
      localStorage.setItem(railLsKey(brandId), String(railWidth));
    } catch {}
  }, [brandId, railWidth]);

  const draggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const next = Math.min(RAIL_MAX, Math.max(RAIL_MIN, e.clientX - rect.left));
      setRailWidth(next);
    }
    function onUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // ── Selection / expansion state ──
  const resolveKeyByTitle = useCallback(
    (title: string | null | undefined): string | null => {
      if (!title) return null;
      const normalised = title.trim().toLowerCase();
      const match = actions.find((a) => a.title.trim().toLowerCase() === normalised);
      return match ? actionKey(match) : null;
    },
    [actions]
  );

  const [selectedActionKey, setSelectedActionKey] = useState<string | null>(() => {
    const fromHighlight = resolveKeyByTitle(highlightActionTitle);
    if (fromHighlight != null) return fromHighlight;
    return actions.length > 0 ? actionKey(actions[0]) : null;
  });

  const [expandedActionKeys, setExpandedActionKeys] = useState<Set<string>>(new Set());

  const [readActionIds, setReadActionIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [viewedInSession, setViewedInSession] = useState<Set<number>>(new Set());
  const viewedInSessionRef = useRef<Set<number>>(new Set());

  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevHighlightTitleRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fixActionsService.getReadStatus({ brandId });
        if (mounted) setReadActionIds(new Set(res.readActionIds));
      } catch {
        // non-fatal
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [brandId]);

  useEffect(() => {
    viewedInSessionRef.current = viewedInSession;
  }, [viewedInSession]);

  useEffect(() => {
    return () => {
      const ids = Array.from(viewedInSessionRef.current);
      if (ids.length > 0) {
        fixActionsService.markActionsRead({ actionIds: ids, brandId }).catch(() => {});
      }
    };
  }, [brandId]);

  useEffect(() => {
    if (actions.length === 0) {
      setSelectedActionKey(null);
      return;
    }
    if (highlightActionTitle) {
      const resolved = resolveKeyByTitle(highlightActionTitle);
      if (resolved != null) {
        setSelectedActionKey(resolved);
        return;
      }
    }
    setSelectedActionKey((prev) => {
      if (prev != null && actions.some((a) => actionKey(a) === prev)) return prev;
      return actionKey(actions[0]);
    });
  }, [actions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (highlightActionTitle === prevHighlightTitleRef.current) return;
    prevHighlightTitleRef.current = highlightActionTitle;
    if (!highlightActionTitle) return;

    const matchedKey = resolveKeyByTitle(highlightActionTitle);
    if (matchedKey == null) return;

    setSelectedActionKey(matchedKey);
    if (layout === "accordion") {
      setExpandedActionKeys((prev) => new Set([...Array.from(prev), matchedKey]));
    }

    const matchedAction = actions.find((a) => actionKey(a) === matchedKey);
    if (matchedAction) {
      setViewedInSession((prev) => new Set([...Array.from(prev), matchedAction.id]));
    }

    const scrollToItem = () => {
      const el = itemRefs.current.get(matchedKey);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return true;
      }
      return false;
    };
    if (!scrollToItem()) {
      const t1 = setTimeout(scrollToItem, 80);
      const t2 = setTimeout(scrollToItem, 220);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [highlightActionTitle, actions, layout]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (action: FixAction) => {
    const key = actionKey(action);
    setViewedInSession((prev) => new Set([...Array.from(prev), action.id]));

    if (layout === "split") {
      setSelectedActionKey(key);
    } else {
      setExpandedActionKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    }
  };

  const getActionTrackerCounts = useCallback(
    (action: FixAction) => {
      const total = action.artifacts?.length ?? 0;
      const tracked = artifactTracker.getForAction(action.id);
      const published = tracked.filter((t) => t.status.toLowerCase() === "published").length;
      return {
        generatedCount: tracked.length,
        publishedCount: published,
        totalArtifacts: total,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackerVersion]
  );

  const selectedAction = useMemo(
    () => actions.find((a) => actionKey(a) === selectedActionKey) ?? null,
    [actions, selectedActionKey]
  );
  const highlightedKey = resolveKeyByTitle(highlightActionTitle);

  const toggleButtonStyle = (active: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: "var(--radius)",
    border: `1px solid ${active ? "var(--alpha-teal-30)" : "var(--border)"}`,
    backgroundColor: active ? "var(--alpha-teal-5)" : "var(--background)",
    color: active ? "var(--alpha-teal)" : "var(--muted-foreground)",
    fontSize: "var(--text-xs)",
    fontWeight: "var(--font-weight-semibold)",
    cursor: active ? "default" : "pointer",
    letterSpacing: "0.02em",
  });

  // Layout toggle — only shown on sm+ because it's pointless on mobile
  // (we force accordion there regardless).
  const LayoutToggle = !isMobile ? (
    <div
      className="inline-flex items-center"
      style={{ gap: 4 }}
      role="tablist"
      aria-label="Inbox layout"
    >
      <button
        onClick={() => setLayout("split")}
        style={toggleButtonStyle(userLayout === "split")}
        aria-pressed={userLayout === "split"}
        title="Split view — list on the left, detail on the right"
      >
        <Columns2 style={{ width: 12, height: 12 }} />
        Split
      </button>
      <button
        onClick={() => setLayout("accordion")}
        style={toggleButtonStyle(userLayout === "accordion")}
        aria-pressed={userLayout === "accordion"}
        title="Accordion view — tap a row to expand inline"
      >
        <LayoutList style={{ width: 12, height: 12 }} />
        Accordion
      </button>
    </div>
  ) : null;

  // Accordion mode (forced on mobile, or chosen by user on desktop)
  if (layout === "accordion") {
    return (
      <div
        ref={containerRef}
        className="flex flex-col"
        style={{ minHeight: "calc(100vh - 260px)" }}
      >
        <div
          className="flex items-center justify-between"
          style={{
            padding: "10px 12px",
            borderBottom: "1px solid var(--border)",
            backgroundColor: "var(--background)",
          }}
        >
          <span
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--muted-foreground)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {actions.length} action{actions.length === 1 ? "" : "s"}
          </span>
          {LayoutToggle}
        </div>

        <div
          className="flex-1 overflow-y-auto"
          style={{ backgroundColor: "var(--muted)", padding: "8px sm:12px" }}
        >
          {actions.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="p-2 sm:p-3">
              {actions.map((action) => {
                const actKey = actionKey(action);
                const isNew =
                  !isLoading &&
                  !readActionIds.has(action.id) &&
                  !viewedInSession.has(action.id);

                const isExpanded = expandedActionKeys.has(actKey);
                const isExternalHighlight =
                  highlightedKey != null && actKey === highlightedKey;
                const { generatedCount, publishedCount, totalArtifacts } =
                  getActionTrackerCounts(action);

                return (
                  <div
                    key={actKey}
                    ref={(el) => {
                      if (el) itemRefs.current.set(actKey, el);
                      else itemRefs.current.delete(actKey);
                    }}
                    style={{ marginBottom: 8 }}
                  >
                    <div
                      style={{
                        borderRadius: "var(--radius)",
                        backgroundColor: "var(--background)",
                        border: "1px solid var(--border)",
                        outline: isExternalHighlight
                          ? "2px solid var(--alpha-teal)"
                          : "2px solid transparent",
                        outlineOffset: "2px",
                        transition: "outline-color 0.4s ease",
                        overflow: "hidden",
                      }}
                    >
                      <ActionItemCompactRow
                        action={action}
                        isSelected={isExpanded}
                        isNew={isNew}
                        generatedCount={publishedCount}
                        anyGeneratedCount={generatedCount}
                        totalArtifacts={totalArtifacts}
                        showExpandIndicator={true}
                        isExpanded={isExpanded}
                        onClick={() => handleRowClick(action)}
                      />

                      {isExpanded && (
                        <div
                          style={{
                            borderTop: "1px solid var(--border)",
                            backgroundColor: "var(--background)",
                          }}
                        >
                          <ActionItemDetailPanel
                            key={`${actKey}-${trackerVersion ?? 0}`}
                            action={action}
                            onExplainWithAI={onExplainWithAI}
                            onGenerateAsset={onGenerateAsset}
                            onNavigateToStudio={onNavigateToStudio}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Split mode (desktop/tablet only — never reached on mobile)
  return (
    <div
      ref={containerRef}
      className="flex flex-col"
      style={{ height: "calc(100vh - 260px)" }}
    >
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel: list */}
        <div
          className="flex flex-col"
          style={{
            width: `${railWidth}px`,
            flexShrink: 0,
            backgroundColor: "var(--muted)",
            borderRight: "1px solid var(--border)",
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
            }}
          >
            <span
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--muted-foreground)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {actions.length} action{actions.length === 1 ? "" : "s"}
            </span>
            {LayoutToggle}
          </div>

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto"
            style={{ padding: "8px" }}
          >
            {actions.map((action, index) => {
              const actKey = actionKey(action);
              const isNew =
                !isLoading &&
                !readActionIds.has(action.id) &&
                !viewedInSession.has(action.id);

              const isSelected = selectedActionKey === actKey;
              const isExternalHighlight =
                highlightedKey != null && actKey === highlightedKey;
              const { generatedCount, publishedCount, totalArtifacts } =
                getActionTrackerCounts(action);

              return (
                <div
                  key={actKey}
                  ref={(el) => {
                    if (el) itemRefs.current.set(actKey, el);
                    else itemRefs.current.delete(actKey);
                  }}
                >
                  <div
                    style={{
                      borderRadius: "var(--radius)",
                      outline: isExternalHighlight
                        ? "2px solid var(--alpha-teal)"
                        : "2px solid transparent",
                      outlineOffset: "2px",
                      transition: "outline-color 0.4s ease",
                    }}
                  >
                    <ActionItemCompactRow
                      action={action}
                      isSelected={isSelected}
                      isNew={isNew}
                      generatedCount={publishedCount}
                      anyGeneratedCount={generatedCount}
                      totalArtifacts={totalArtifacts}
                      onClick={() => handleRowClick(action)}
                    />
                  </div>

                  {index < actions.length - 1 && (
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "var(--border)",
                        margin: "3px 0",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Drag handle */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize action list"
          onMouseDown={startDrag}
          style={{
            width: 6,
            marginLeft: -3,
            marginRight: -3,
            cursor: "col-resize",
            backgroundColor: "transparent",
            zIndex: 2,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              bottom: 0,
              width: 1,
              backgroundColor: "var(--border)",
              transform: "translateX(-50%)",
              pointerEvents: "none",
            }}
          />
        </div>

        {/* Right panel: detail */}
        <div
          className="flex-1 overflow-hidden"
          style={{ backgroundColor: "var(--background)", minWidth: 0 }}
        >
          {selectedAction ? (
            <ActionItemDetailPanel
              key={`${actionKey(selectedAction)}-${trackerVersion ?? 0}`}
              action={selectedAction}
              onExplainWithAI={onExplainWithAI}
              onGenerateAsset={onGenerateAsset}
              onNavigateToStudio={onNavigateToStudio}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-6">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: "var(--muted)" }}
      >
        <Eye className="w-8 h-8" style={{ color: "var(--muted-foreground)" }} />
      </div>
      <h4
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
          color: "var(--foreground)",
          marginBottom: "6px",
        }}
      >
        Select an action to view details
      </h4>
      <p
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--muted-foreground)",
          maxWidth: "400px",
        }}
      >
        Choose an action item from the list to see the full strategic framework,
        optimization assets, and generation options.
      </p>
    </div>
  );
}